import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { moveItemInArray, NxReorderEvent } from '../../directives/reorder';

export interface FieldConfig {
  field: string;
  label: string;
}

@Component({
  selector: 'nx-field-selector',
  templateUrl: './field-selector.component.html',
  styleUrls: ['./field-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldSelectorComponent {
  // Inputs
  readonly availableFields = input.required<FieldConfig[]>();
  readonly displayedFields = input.required<string[]>();
  readonly defaultFields = input.required<string[]>();
  readonly showMetadata = input<boolean>(false);

  // Outputs
  @Output() displayedFieldsChange = new EventEmitter<string[]>();
  @Output() showMetadataChange = new EventEmitter<boolean>();

  // Internal state for minimum field enforcement
  readonly canRemoveFields = computed(() => this.displayedFields().length > 1);

  // Computed hidden fields
  readonly hiddenFields = computed(() => {
    const displayed = this.displayedFields();
    return this.availableFields()
      .map(f => f.field)
      .filter(field => !displayed.includes(field));
  });

  /**
   * Get label for a field
   */
  getFieldLabel(field: string): string {
    const config = this.availableFields().find(f => f.field === field);
    return config?.label ?? field;
  }

  /**
   * Handle drag-drop reordering
   */
  onFieldReordered(event: NxReorderEvent): void {
    const fields = [...this.displayedFields()];
    moveItemInArray(fields, event.previousIndex, event.currentIndex);
    this.displayedFieldsChange.emit(fields);
  }

  /**
   * Remove field from displayed list
   */
  onRemoveField(event: Event, field: string): void {
    event.stopPropagation();
    
    // Enforce minimum 1 field
    if (!this.canRemoveFields()) {
      return;
    }

    const updated = this.displayedFields().filter(f => f !== field);
    this.displayedFieldsChange.emit(updated);
  }

  /**
   * Add field to displayed list
   */
  onAddField(field: string): void {
    const updated = [...this.displayedFields(), field];
    this.displayedFieldsChange.emit(updated);
  }

  /**
   * Reset to default fields
   */
  onResetFields(): void {
    this.displayedFieldsChange.emit([...this.defaultFields()]);
  }
}
